import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  ctaIntent,
  formatCompactBytes,
  formatPlanPrice,
  perMonthCents,
  planFundsAi,
} from '../utils/planDisplay'
import type { PaidPlanCode, PlanCode, PlanDocument } from '@/lib/types/subscription'
import { BILLING_INTERVALS, PLAN_CODES, isPaidPlanCode } from '@/lib/types/subscription'
import { cn } from '@/lib/utils'

type Props = {
  plan: PlanDocument
  isCurrentPlan: boolean
  currentPlanCode: PlanCode | null
  trialEligibility?: Partial<Record<PaidPlanCode, boolean>>
  billingInterval: string
  actionInProgress: boolean
  disabled?: boolean
  disabledTitle?: string
  onSelect: (planId: string, billingInterval: string) => void
}

/**
 * Lighter plan card (design 15c). Prices, limits and trial wording come from
 * the plan catalogue; the recommended plan (the seeded `highlightText`) gets
 * the brand border and CTA.
 */
export function UpgradePlanCard({
  plan,
  isCurrentPlan,
  currentPlanCode,
  trialEligibility,
  billingInterval,
  actionInProgress,
  disabled = false,
  disabledTitle,
  onSelect,
}: Props) {
  const { t } = useTranslation('settings')
  const isAnnual = billingInterval === BILLING_INTERVALS.ANNUAL
  const isTeamPlan = plan.code === PLAN_CODES.TEAM
  const trialEligible =
    isPaidPlanCode(plan.code) && (trialEligibility?.[plan.code] ?? false)
  const recommended = Boolean(plan.highlightText) && !isCurrentPlan

  const price = formatPlanPrice(perMonthCents(plan, isAnnual), plan.currency)
  const billedNote = isAnnual
    ? t(isTeamPlan ? 'subscription.planCard.perSeatBilledYearly' : 'subscription.planCard.billedYearly', {
        amount: formatPlanPrice(plan.priceAnnual, plan.currency),
      })
    : t(isTeamPlan ? 'subscription.planCard.perSeatBilledMonthly' : 'subscription.planCard.billedMonthly')

  const features = planFeatures(plan, trialEligible, t)

  const intent = ctaIntent({ target: plan, isCurrentPlan, currentPlanCode, trialEligible })
  const ctaLabel = {
    current: t('subscription.planCard.cta.current'),
    getStarted: t('subscription.planCard.cta.getStarted'),
    downgradeFree: t('subscription.planCard.cta.downgradeFree'),
    switch: t('subscription.planCard.switchTo', { plan: plan.name }),
    upgrade: t('subscription.planCard.cta.upgrade', { plan: plan.name }),
    downgrade: t('subscription.planCard.cta.downgrade', { plan: plan.name }),
    trial: t('subscription.planCard.cta.trial'),
  }[intent.kind]

  return (
    <div
      className={cn(
        'flex flex-col rounded-[14px] border bg-card p-5',
        recommended && 'border-brand',
        isCurrentPlan && 'border-foreground/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold">{plan.name}</h3>
        {isCurrentPlan ? (
          <span className="rounded-full bg-foreground px-2 py-0.5 text-[11px] font-semibold text-background">
            {t('subscription.badge.current')}
          </span>
        ) : (
          plan.highlightText && (
            <span className="rounded-full bg-brand-tint px-2 py-0.5 text-[11px] font-semibold text-brand-tint-foreground">
              {plan.highlightText}
            </span>
          )
        )}
      </div>

      <p className="mt-3 flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-[32px] leading-none font-bold tracking-tight">{price}</span>
        <span className="text-[13px] text-muted-foreground">{billedNote}</span>
      </p>

      <ul className="mt-4 flex-1 space-y-1.5">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-[13px] text-secondary-foreground">
            <Check className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => onSelect(plan.id, billingInterval)}
        disabled={isCurrentPlan || actionInProgress || disabled}
        title={disabled ? disabledTitle : undefined}
        className={cn(
          'mt-5 inline-flex h-10 w-full items-center justify-center rounded-lg text-sm font-semibold transition-opacity disabled:cursor-not-allowed',
          isCurrentPlan
            ? 'border bg-card text-muted-foreground'
            : recommended
              ? 'bg-brand text-brand-foreground hover:opacity-90 disabled:opacity-50'
              : 'bg-foreground text-background hover:opacity-90 disabled:opacity-50',
        )}
      >
        {ctaLabel}
      </button>
    </div>
  )
}

type TFn = (key: string, options?: Record<string, unknown>) => string

/** Feature lines built from the plan's limits — no marketing copy invented. */
function planFeatures(plan: PlanDocument, trialEligible: boolean, t: TFn): Array<string> {
  const lines: Array<string> = []
  const { tasks, projects, storage, members, guests, maxFileSizeBytes } = plan.limits

  if (tasks === -1 && projects === -1 && storage === -1) {
    lines.push(t('subscription.features.unlimitedCore'))
  } else {
    lines.push(tasks === -1 ? t('subscription.features.unlimitedTasks') : t('subscription.features.tasks', { count: tasks }))
    lines.push(projects === -1 ? t('subscription.features.unlimitedProjects') : t('subscription.features.projects', { count: projects }))
    lines.push(storage === -1 ? t('subscription.features.unlimitedStorage') : t('subscription.features.storage', { size: formatCompactBytes(storage) }))
  }

  if (maxFileSizeBytes > 0) {
    lines.push(t('subscription.features.perFile', { size: formatCompactBytes(maxFileSizeBytes) }))
  }

  if (planFundsAi(plan)) {
    const hasAiTrial = trialEligible && (plan.limits.aiTrialChatTokens ?? 0) > 0
    lines.push(hasAiTrial ? t('subscription.planCard.aiIncludedWithTrial') : t('subscription.planCard.aiIncluded'))
  }

  if (members === -1) lines.push(t('subscription.features.unlimitedMembers'))
  else if (members > 0) lines.push(t('subscription.features.members', { count: members }))

  if (guests === -1) lines.push(t('subscription.features.unlimitedGuests'))
  else if (guests > 0) lines.push(t('subscription.features.guests', { count: guests }))

  return lines
}
